import { useState, useEffect, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from 'recharts';
import { useTheme } from '@/contexts/ThemeContext';

interface DataPoint {
  time: string;
  price: number;
  real: boolean;
}

function generateInitialData(): DataPoint[] {
  const data: DataPoint[] = [];
  let price = 44800;
  const now = new Date();
  for (let i = 0; i < 60; i++) {
    const change = (Math.random() - 0.48) * 180;
    price += change;
    const t = new Date(now.getTime() - (60 - i) * 60000);
    data.push({
      time: t.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
      price: Number(price.toFixed(2)),
      real: false,
    });
  }
  return data;
}

const WS_URL = 'wss://stream.binance.com:9443/ws/btcusdt@ticker';

export default function TradingChart() {
  const { isDark } = useTheme();
  const [data, setData] = useState<DataPoint[]>(generateInitialData);
  const [connected, setConnected] = useState(false);
  const dataRef = useRef<DataPoint[]>(data);
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  // WebSocket: real-time BTC price feed
  useEffect(() => {
    let ws: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let mounted = true;

    const connect = () => {
      if (!mounted) return;
      try {
        ws = new WebSocket(WS_URL);
        wsRef.current = ws;

        ws.onopen = () => {
          if (!mounted) return;
          setConnected(true);
        };

        ws.onmessage = (event) => {
          if (!mounted) return;
          try {
            const msg = JSON.parse(event.data);
            if (msg.c) {
              const price = parseFloat(msg.c);
              const now = new Date();
              const timeStr = now.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false,
              });

              setData((prev) => {
                const next = [...prev];
                next.shift();
                next.push({ time: timeStr, price, real: true });
                return next;
              });
            }
          } catch {
            // ignore
          }
        };

        ws.onerror = () => {
          // silent
        };

        ws.onclose = () => {
          if (!mounted) return;
          setConnected(false);
          wsRef.current = null;
          reconnectTimer = setTimeout(connect, 5000);
        };
      } catch {
        reconnectTimer = setTimeout(connect, 5000);
      }
    };

    connect();

    return () => {
      mounted = false;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws && ws.readyState !== WebSocket.CLOSED) ws.close();
    };
  }, []);

  const { currentPrice, startPrice, change } = useMemo(() => {
    const cur = data[data.length - 1]?.price ?? 45230;
    const start = data[0]?.price ?? 44800;
    return {
      currentPrice: cur,
      startPrice: start,
      change: ((cur - start) / start) * 100,
    };
  }, [data]);

  const tooltipStyle = isDark
    ? {
        backgroundColor: '#0a0a0a',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: '8px',
        fontFamily: 'JetBrains Mono',
        fontSize: '12px',
        color: '#fff',
      }
    : {
        backgroundColor: '#ffffff',
        border: '1px solid rgba(0,0,0,0.1)',
        borderRadius: '8px',
        fontFamily: 'JetBrains Mono',
        fontSize: '12px',
        color: '#111',
      };

  const gridStroke = isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.06)';
  const tickFill = isDark ? '#666' : '#999';
  const priceTextColor = isDark ? 'text-white' : 'text-[#111]';
  const lineColor = change >= 0 ? '#00ff88' : '#ff4444';

  return (
    <div className="relative w-full h-full min-h-[280px] md:min-h-[320px]">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 40, right: 0, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={lineColor} stopOpacity={0.3} />
              <stop offset="95%" stopColor={lineColor} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
          <XAxis
            dataKey="time"
            axisLine={false}
            tickLine={false}
            tick={{ fill: tickFill, fontSize: 10, fontFamily: 'JetBrains Mono' }}
            interval={14}
          />
          <YAxis
            domain={['auto', 'auto']}
            axisLine={false}
            tickLine={false}
            tick={{ fill: tickFill, fontSize: 10, fontFamily: 'JetBrains Mono' }}
            width={60}
            tickFormatter={(value: number) => `$${value.toLocaleString()}`}
          />
          <Tooltip
            contentStyle={tooltipStyle}
            formatter={(value: number) => [`$${value.toLocaleString()}`, 'Price']}
            labelStyle={{ color: isDark ? '#999' : '#666' }}
          />
          <Area
            type="monotone"
            dataKey="price"
            stroke={lineColor}
            strokeWidth={2}
            fillOpacity={1}
            fill="url(#colorPrice)"
            animationDuration={300}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>

      {/* Price overlay */}
      <div className="absolute top-2 left-2">
        <div className={`text-[10px] font-mono uppercase tracking-wider ${isDark ? 'text-[#666]' : 'text-[#999]'}`}>
          BTC-PERP
          {connected && (
            <span className="inline-flex items-center gap-1 ml-2 text-[#00ff88]">
              <span className="w-1 h-1 rounded-full bg-[#00ff88] animate-pulse" />
              LIVE
            </span>
          )}
        </div>
        <div className="flex items-baseline gap-2 mt-0.5">
          <span className={`text-lg font-mono font-semibold ${priceTextColor}`}>
            ${currentPrice.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </span>
          <motion.span
            key={change >= 0 ? 'positive' : 'negative'}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            className={`text-xs font-mono ${change >= 0 ? 'text-[#00ff88]' : 'text-[#ff4444]'}`}
          >
            {change >= 0 ? '+' : ''}{change.toFixed(2)}%
          </motion.span>
        </div>
      </div>

      {/* Scanning line effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none rounded-lg">
        <motion.div
          initial={{ top: '0%' }}
          animate={{ top: '100%' }}
          transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
          className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00ff88]/30 to-transparent"
        />
      </div>
    </div>
  );
}